import React, { useState, createContext, useContext, useEffect } from 'react';
import { useMutation } from '@edreamsodigeo/frontend-api-client';
import { ITINERARY_SEARCH_ALTERNATIVES } from 'clientState/providers/operations/itinerarySearchAlternatives';
import {
  ItinerarySearchAlternativesMutation,
  ItinerarySearchAlternativesMutationVariables,
  ItinerarySearchResult,
  ItineraryFragment,
} from 'generated/graphql';
import { AlternativeItineraryMutationContextType } from './types';
import { usePerformedSearchRequest } from '../SearchRequestProvider';
import {
  useLogFrontendApiError,
  useTracking,
} from '@edreamsodigeo/frontend-core';
import { SEARCH_ID_DIMENSION } from '../../hooks/useSetSearchGACustomDimensions/config';
import { transformItinerarySearchResponse } from '../SearchResponseProvider/transformItinerarySearchResponse';
import itineraryResolver from 'clientState/resolvers/itinerary';

const AlternativeItineraryMutationContext =
  createContext<AlternativeItineraryMutationContextType>({
    alternativeMutationLaunched: false,
    alternativeSearchId: null,
    alternativeItineraryId: null,
    alternativeItineraries: null,
    alternativeItineraryObject: null,
    loading: false,
    errors: null,
    mutate: () => {},
    setAlternativeMutationLaunched: () => {},
  });

const updateAlternativeItineraries = (
  alternativeItineraries,
  alertnativeItinerary
) => {
  const existInList = alternativeItineraries.some(
    (itinerary) => itinerary.id === alertnativeItinerary.id
  );

  const alternativeReplaced = existInList
    ? alternativeItineraries.map((itinerary) => {
        return itinerary.id === alertnativeItinerary.id
          ? alertnativeItinerary
          : itinerary;
      })
    : [...alternativeItineraries, alertnativeItinerary];

  return alternativeReplaced;
};

const AlternativeItineraryMutationProvider = ({ children }) => {
  const [alternativeMutationLaunched, setAlternativeMutationLaunched] =
    useState<boolean>(false);
  const [alternativeSearchId, setAlternativeSearchId] = useState<number | null>(
    null
  );
  const [alternativeItineraryId, setAlternativeItineraryId] =
    useState<string>('');
  const [alternativeItineraries, setAlternativeItineraries] = useState<
    ItinerarySearchResult[]
  >([]);
  const [alternativeItineraryObject, setAlternativeItineraryObject] =
    useState<any>(null);

  const searchRequest = usePerformedSearchRequest();
  const { setCustomDimension } = useTracking();

  const mutationResult = useMutation<
    ItinerarySearchAlternativesMutation,
    ItinerarySearchAlternativesMutationVariables
  >(ITINERARY_SEARCH_ALTERNATIVES);
  const { mutate, loading, data, errors } = mutationResult;

  useLogFrontendApiError(mutationResult);

  useEffect(() => {
    if (searchRequest) {
      setAlternativeSearchId(null);
      setAlternativeItineraries([]);
    }
  }, [searchRequest]);

  useEffect(() => {
    if (data?.itinerarySearchAlternatives) {
      const searchId = data.itinerarySearchAlternatives.searchId;
      const alternativeItinerary =
        data.itinerarySearchAlternatives.itineraries[0];
      const alternativeItineraryId = alternativeItinerary?.id || '';

      const parsedAlternativeResponse = transformItinerarySearchResponse({
        data: {
          itinerarySearch: {
            ...data.itinerarySearchAlternatives,
            campaignsItineraryLists: [],
            insuranceOffers: [],
            campaignConfigs: [],
            fees: [],
          },
        },
      });
      const alternativeItineraryResolved = itineraryResolver(
        parsedAlternativeResponse?.search.itineraries[0] as ItineraryFragment,
        0
      );

      setAlternativeMutationLaunched(false);
      setAlternativeSearchId(searchId);
      setCustomDimension(SEARCH_ID_DIMENSION, `${searchId}`);
      setAlternativeItineraryId(alternativeItineraryId);
      setAlternativeItineraryObject(alternativeItineraryResolved);
      setAlternativeItineraries((alternativeItineraries) =>
        updateAlternativeItineraries(
          alternativeItineraries,
          alternativeItinerary
        )
      );
    }
  }, [data, setCustomDimension]);

  return (
    <AlternativeItineraryMutationContext.Provider
      value={{
        alternativeMutationLaunched,
        alternativeSearchId,
        alternativeItineraryId,
        alternativeItineraryObject,
        alternativeItineraries,
        loading,
        errors,
        mutate,
        setAlternativeMutationLaunched,
      }}
    >
      {children}
    </AlternativeItineraryMutationContext.Provider>
  );
};

export const useAlternativeItineraryMutation = () =>
  useContext(AlternativeItineraryMutationContext);

export default AlternativeItineraryMutationProvider;
